import browser from '@openclassrooms/utils/browser';

export type Environment = 'dev' | 'stage' | 'preprod' | 'prod' | 'demo';

type EnvironmentChecker = {
  isProd: () => boolean;
  isStage: () => boolean;
  isPreprod: () => boolean;
  isOnTheFlyStage: () => boolean;
  isNotStage: () => boolean;
  isNotProd: () => boolean;
  isDev: () => boolean;
  isNotDev: () => boolean;
  isDemo: () => boolean;
  getEnvironment: () => Environment;
};

const createEnvironmentChecker = (host: string | undefined): EnvironmentChecker => {
  const isProd = () => host === 'openclassrooms.com';

  const isStage = () => Boolean(host?.match(/^stage/)) || Boolean(host?.match(/^staging/));

  const isPreprod = () => Boolean(host?.match(/^preprod/));
  const isOnTheFlyStage = () => Boolean(host?.match(/stage-oc-\d+/));
  const isNotStage = () => !isStage();
  const isNotProd = () => !isProd();
  const isDev = () => isNotStage() && isNotProd();
  const isNotDev = () => !isDev();
  const isDemo = () => Boolean(host?.match(/^demo/));

  const getEnvironment = (): Environment => {
    if (isProd()) {
      return 'prod';
    }

    if (isPreprod()) {
      return 'preprod';
    }

    if (isStage() || isOnTheFlyStage()) {
      return 'stage';
    }

    if (isDemo()) {
      return 'demo';
    }

    return 'dev';
  };

  return {
    isProd,
    isStage,
    isPreprod,
    isOnTheFlyStage,
    isNotStage,
    isNotProd,
    isDev,
    isNotDev,
    isDemo,
    getEnvironment,
  };
};

export const createEnv = (host: string): EnvironmentChecker => createEnvironmentChecker(host);

const getBrowserHost = () => browser?.window?.location.host;

let envChecker = createEnvironmentChecker(getBrowserHost());

export const setEnvironmentHost = (host?: string): EnvironmentChecker => {
  envChecker = createEnvironmentChecker(host ?? getBrowserHost());
  return envChecker;
};

export function isProd() {
  return envChecker.isProd();
}

export function isStage() {
  return envChecker.isStage();
}

export function isPreprod() {
  return envChecker.isPreprod();
}

export function isOnTheFlyStage() {
  return envChecker.isOnTheFlyStage();
}

export function isNotStage() {
  return envChecker.isNotStage();
}

export function isNotProd() {
  return envChecker.isNotProd();
}

export function isDev() {
  return envChecker.isDev();
}

export function isNotDev() {
  return envChecker.isNotDev();
}

export function isDemo() {
  return envChecker.isDemo();
}

export function getEnvironment(): Environment {
  return envChecker.getEnvironment();
}
